import { Box, Button, Grid, IconButton, Typography } from '@mui/material';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CreateDestinationFormValues } from '../../organisms/create-destination/create-destination-form';

import CautionCard from '@/components/atoms/caution-card';
import { InputField } from '@/components/atoms/input-field';
import StepTitle from '@/components/atoms/step-title';
import {
  CreateDestinationContext,
  CreateDestinationEvent,
} from '@/features/destinations/utils/machine/create-destination-machine';
import { Trash } from '@/icons/trash';

interface Props {
  context: CreateDestinationContext;
  send: (event: CreateDestinationEvent) => void;
}

export const DirectionTips = ({ context, send }: Props) => {
  const { t } = useTranslation(['common', 'createDestination', 'destination']);

  const {
    control,
    formState: { errors },
  } = useFormContext<CreateDestinationFormValues>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'directionTips',
  });

  const directionTipsError =
    typeof errors.directionTips?.message === 'string' ? errors.directionTips.message : undefined;

  return (
    <div>
      <Box
        sx={{
          display: 'grid',
          gap: 1,
          gridAutoFlow: 'row',
        }}>
        <StepTitle
          title={t('common:stepTitle', {
            step: context.step,
            title: t(context.content),
          })}
        />

        <CautionCard
          title={t('common:optional')}
          description={t('createDestination:optionalStepDescription')}
          sx={{ mb: 2 }}
        />

        <Grid container spacing={1.5}>
          <Grid item md={0.4} xs={12}>
            <Typography variant="subtitle2">#</Typography>
          </Grid>
          <Grid item md={3.7} xs={12}>
            <Typography variant="subtitle2">{t('destination:translations.polish')}</Typography>
          </Grid>
          <Grid item md={3.7} xs={12}>
            <Typography variant="subtitle2">{t('destination:translations.english')}</Typography>
          </Grid>
          <Grid item md={3.7} xs={12}>
            <Typography variant="subtitle2">{t('destination:translations.ukrainian')}</Typography>
          </Grid>
          <Grid item md={0.4} xs={12} />

          {fields.map((field, index) => (
            <Grid container item spacing={1.5} key={field.id} alignItems="center">
              <Grid item md={0.4} xs={12}>
                <Typography color="text.secondary" variant="body2">
                  {index + 1}
                </Typography>
              </Grid>
              <Grid item md={3.7} xs={12}>
                <Controller
                  control={control}
                  name={`directionTips.${index}.pl`}
                  render={({ field, fieldState: { error } }) => (
                    <InputField
                      {...field}
                      fullWidth
                      error={!!error?.message}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item md={3.7} xs={12}>
                <Controller
                  control={control}
                  name={`directionTips.${index}.en`}
                  render={({ field, fieldState: { error } }) => (
                    <InputField
                      {...field}
                      fullWidth
                      error={!!error?.message}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item md={3.7} xs={12}>
                <Controller
                  control={control}
                  name={`directionTips.${index}.ua`}
                  render={({ field, fieldState: { error } }) => (
                    <InputField
                      {...field}
                      fullWidth
                      error={!!error?.message}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item md={0.4} xs={12}>
                <IconButton
                  color="error"
                  type="button"
                  size="small"
                  aria-label={t('common:button.delete')}
                  onClick={() => remove(index)}>
                  <Trash />
                </IconButton>
              </Grid>
            </Grid>
          ))}

          {directionTipsError && (
            <Grid item xs={12}>
              <Typography color="error.main" variant="caption">
                {directionTipsError}
              </Typography>
            </Grid>
          )}
        </Grid>

        <Box sx={{ mt: 1 }}>
          <Button
            color="primary"
            variant="outlined"
            type="button"
            disabled={fields.length >= 50}
            onClick={() => append({ pl: '', en: '', ua: '' })}>
            {t('createDestination:form.addDirectionTipRow')}
          </Button>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 3,
          }}>
          <Button
            color="primary"
            onClick={() => send({ type: 'BACK' })}
            size="large"
            sx={{ mr: 2 }}
            type="button"
            variant="text">
            {t('common:button.back')}
          </Button>
          <Button
            color="primary"
            size="large"
            type="button"
            variant="contained"
            onClick={() => send({ type: 'NEXT' })}>
            {t('common:button.nextStep')}
          </Button>
        </Box>
      </Box>
    </div>
  );
};
